import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconInfoCircle,
  IconMapPin,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import type {
  Block,
  HarvestScheduleResponse,
  MandorCensus,
  PlanLineSource,
  Worker,
} from "../lib/types";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function addDays(iso: string, days: number) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const HORIZON_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Next 3 days", days: 3 },
  { label: "Next 7 days", days: 7 },
  { label: "Next 14 days", days: 14 },
] as const;

const SOURCE_BADGE: Record<PlanLineSource, string> = {
  AUTO: "bg-emerald-100 text-emerald-800",
  EDITED: "bg-amber-100 text-amber-800",
  SKIPPED: "bg-slate-100 text-slate-700",
  OVERRIDDEN: "bg-red-100 text-red-800",
  MANUAL: "bg-blue-100 text-blue-800",
};

// Schedule-line lifecycle (harvest_plan_line.status) — this is the
// planning/approval state of the line itself, NOT the physical FFB
// pipeline (bunch audit → mech pickup → SPB dispatch), which each
// have their own separate status fields on other tables.
const STATUS_META: Record<
  "DRAFT" | "CONFIRMED" | "IN_PROGRESS" | "DONE" | "CANCELLED",
  { label: string; text: string; desc: string }
> = {
  DRAFT: {
    label: "Draft",
    text: "text-slate-600",
    desc: "Target plan · candidate line, not yet approved",
  },
  CONFIRMED: {
    label: "Confirmed",
    text: "text-blue-700",
    desc: "Planner approved · gang locked in, ready to execute",
  },
  IN_PROGRESS: {
    label: "In progress",
    text: "text-amber-700",
    desc: "Mandor started the harvest round on the block today",
  },
  DONE: {
    label: "Done",
    text: "text-emerald-700",
    desc: "Round completed · actual date/bunches recorded",
  },
  CANCELLED: {
    label: "Cancelled",
    text: "text-red-700",
    desc: "Line cancelled",
  },
};

// Renders a compact worker list (e.g. "Ali +2") from a jsonb array of
// worker IDs on a plan line. Shows "—" when the array is empty.
function renderWorkerListShort(
  ids: unknown,
  workerById: Map<string, Worker>,
): string {
  if (!Array.isArray(ids) || ids.length === 0) return "—";
  const first = workerById.get(String(ids[0]))?.name ?? "?";
  return ids.length > 1 ? `${first} +${ids.length - 1}` : first;
}
function renderWorkerListTooltip(
  ids: unknown,
  workerById: Map<string, Worker>,
  role: string,
): string {
  if (!Array.isArray(ids) || ids.length === 0) return `No ${role} assigned`;
  const names = ids
    .map((id) => workerById.get(String(id))?.name ?? "?")
    .join(", ");
  return `${ids.length} ${role}(s): ${names}`;
}

/**
 * Harvest Planning — continuous rolling schedule.
 *
 * No fixed weekly boundary. Each block has a `targetHarvestDate` that
 * shifts continuously as agents / users react to census updates,
 * weather, attendance, and actual harvest variance. The horizon selector
 * is a view filter, not a plan container.
 *
 * Sections:
 *   1. Horizon control + Recalc + KPI tiles
 *   2. Mandor census entry (rich fields — ring 1/2/3, GPS, palms, est maturity)
 *   3. Scheduled lines table with source badges + inline edit
 *   4. Unscheduled candidates (blocks with mature census but no plan yet)
 */
export function HarvestPlanning() {
  const [horizonDays, setHorizonDays] = useState<number>(3);
  const [customTo, setCustomTo] = useState<string>(addDays(todayISO(), 3));
  const [customMode, setCustomMode] = useState(false);

  const [showCensusForm, setShowCensusForm] = useState(false);
  const [editingCensus, setEditingCensus] = useState<MandorCensus | null>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [completingLineId, setCompletingLineId] = useState<string | null>(null);
  const [censusError, setCensusError] = useState<string | null>(null);
  const [censusSuccess, setCensusSuccess] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Items (13) + (14): filters shared by census + schedule sections.
  const [filterEstateId, setFilterEstateId] = useState<string>("ALL");
  const [filterDivisionId, setFilterDivisionId] = useState<string>("ALL");
  const [filterBlockId, setFilterBlockId] = useState<string>("ALL");
  const [filterGangId, setFilterGangId] = useState<string>("ALL");
  const [filterStatus, setFilterStatus] = useState<string>("ALL");
  const [filterCensusFrom, setFilterCensusFrom] = useState<string>("");
  const [filterCensusTo, setFilterCensusTo] = useState<string>("");

  const from = todayISO();
  const to = customMode ? customTo : addDays(from, horizonDays);

  const qc = useQueryClient();

  const scheduleQ = useQuery({
    queryKey: ["harvest-schedule", from, to],
    queryFn: async () => {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL ?? ""}/v1/harvest-schedule?from=${from}&to=${to}`,
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`Schedule fetch failed: ${res.status} ${body}`);
      }
      return (await res.json()) as HarvestScheduleResponse;
    },
  });

  // Item (1): blocks created in Land Management should sync here.
  // staleTime=0 + refetchOnMount forces a fresh fetch every time the
  // Harvesting tab is opened, so a newly created block appears without
  // page reload.
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
    staleTime: 0,
    refetchOnMount: true,
  });
  const workersQ = useQuery({
    queryKey: ["workers"],
    queryFn: () => api.list<Worker>("/v1/workers?limit=500"),
  });
  // Show census entries from the last 30 days so a newly-created census
  // (which may be dated today, yesterday, or a back-fill for an earlier
  // date) always appears. Previously fetched only from=today, which
  // hid any census with a past date the user typed in.
  const censusFromDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);
  const censusQ = useQuery({
    queryKey: ["mandor-census-recent"],
    queryFn: () =>
      api.list<MandorCensus>(`/v1/mandor-census?from=${censusFromDate}`),
    staleTime: 0,
    refetchOnMount: true,
  });
  // Item (1): estates/divisions/gangs also need to sync — a newly created
  // estate in Land Management should immediately appear in the Estate
  // filter dropdown here without full page reload.
  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () =>
      api.list<{ id: string; code: string; name: string; estateId: string }>(
        "/v1/divisions",
      ),
    staleTime: 0,
    refetchOnMount: true,
  });
  const estatesQ = useQuery({
    queryKey: ["estates"],
    queryFn: () =>
      api.list<{ id: string; code: string; name: string }>("/v1/estates"),
    staleTime: 0,
    refetchOnMount: true,
  });
  const gangsQ = useQuery({
    queryKey: ["gangs"],
    queryFn: () =>
      api.list<{ id: string; code: string; name: string }>("/v1/gangs"),
    staleTime: 0,
    refetchOnMount: true,
  });

  const plantedBlocks = useMemo(
    () => (blocksQ.data?.data ?? []).filter((b) => b.category === "PLANTED"),
    [blocksQ.data],
  );
  const blockById = useMemo(() => {
    const m = new Map<string, Block>();
    for (const b of plantedBlocks) m.set(b.id, b);
    return m;
  }, [plantedBlocks]);

  // Division → estate map for filter cascade.
  const divisionEstateId = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of divisionsQ.data?.data ?? []) m.set(d.id, d.estateId);
    return m;
  }, [divisionsQ.data]);

  // Division id → code lookup for census-table "Division" column.
  const divisionCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of divisionsQ.data?.data ?? []) m.set(d.id, d.code);
    return m;
  }, [divisionsQ.data]);

  // Divisions available when a specific estate is selected.
  const availableDivisions = useMemo(() => {
    const all = divisionsQ.data?.data ?? [];
    return filterEstateId === "ALL"
      ? all
      : all.filter((d) => d.estateId === filterEstateId);
  }, [divisionsQ.data, filterEstateId]);

  // Reset division filter if it doesn't belong to the current estate.
  useEffect(() => {
    if (
      filterDivisionId !== "ALL" &&
      !availableDivisions.some((d) => d.id === filterDivisionId)
    ) {
      setFilterDivisionId("ALL");
    }
  }, [availableDivisions, filterDivisionId]);

  // Blocks available given the current estate/division filters.
  const availableBlocks = useMemo(() => {
    let list = plantedBlocks;
    if (filterDivisionId !== "ALL") {
      list = list.filter((b) => b.divisionId === filterDivisionId);
    } else if (filterEstateId !== "ALL") {
      list = list.filter(
        (b) => divisionEstateId.get(b.divisionId) === filterEstateId,
      );
    }
    return list;
  }, [plantedBlocks, filterEstateId, filterDivisionId, divisionEstateId]);

  // Reset block filter if it doesn't belong to the current estate/division.
  useEffect(() => {
    if (
      filterBlockId !== "ALL" &&
      !availableBlocks.some((b) => b.id === filterBlockId)
    ) {
      setFilterBlockId("ALL");
    }
  }, [availableBlocks, filterBlockId]);

  // Predicate applied to both census + schedule rows.
  const matchesBlockFilter = (blockId: string): boolean => {
    if (filterBlockId !== "ALL") return blockId === filterBlockId;
    if (filterEstateId === "ALL" && filterDivisionId === "ALL") return true;
    const block = blockById.get(blockId);
    if (!block) return true; // show; can't determine, don't hide silently
    if (filterDivisionId !== "ALL" && block.divisionId !== filterDivisionId)
      return false;
    if (filterEstateId !== "ALL") {
      if (divisionEstateId.get(block.divisionId) !== filterEstateId) return false;
    }
    return true;
  };

  // Item (6): last census date per block (from all census entries in cache).
  const lastCensusByBlock = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of censusQ.data?.data ?? []) {
      const prev = m.get(c.blockId);
      if (!prev || c.censusDate > prev) m.set(c.blockId, c.censusDate);
    }
    return m;
  }, [censusQ.data]);

  /**
   * Census rows grouped by block, each block's rows sorted by date desc.
   * Also computes the "previous inspection date" for each row so we can
   * display gap-since-last-inspection alongside every census entry.
   */
  const censusGroupedByBlock = useMemo(() => {
    const rows = (censusQ.data?.data ?? [])
      .filter((c) => matchesBlockFilter(c.blockId))
      .filter((c) => !filterCensusFrom || c.censusDate >= filterCensusFrom)
      .filter((c) => !filterCensusTo || c.censusDate <= filterCensusTo);

    // Group by blockId
    const byBlock = new Map<string, MandorCensus[]>();
    for (const c of rows) {
      const arr = byBlock.get(c.blockId) ?? [];
      arr.push(c);
      byBlock.set(c.blockId, arr);
    }
    // Sort each group by census date desc + compute prior-date per row.
    // A block group ends with a synthetic "subtotal" row aggregating the
    // block's ring/bunch/palm counts across every census on that block.
    type FlatRow =
      | {
          kind: "data";
          row: MandorCensus;
          groupFirst: boolean;
          groupLast: boolean;
          priorDate: string | null;
        }
      | {
          kind: "subtotal";
          blockId: string;
          totals: {
            ring1: number;
            ring2: number;
            ring3: number;
            totalBunches: number;
            palmsInspected: number;
            censusCount: number;
          };
        };
    const flat: FlatRow[] = [];
    // Deterministic block order: by block.code (fallback to blockId)
    const orderedBlockIds = Array.from(byBlock.keys()).sort((a, b) => {
      const ba = blockById.get(a)?.code ?? a;
      const bb = blockById.get(b)?.code ?? b;
      return ba.localeCompare(bb);
    });
    for (const bId of orderedBlockIds) {
      const list = byBlock.get(bId)!.sort((x, y) =>
        y.censusDate.localeCompare(x.censusDate),
      );
      let ring1 = 0,
        ring2 = 0,
        ring3 = 0,
        palmsInspected = 0;
      list.forEach((row, idx) => {
        const priorDate = idx < list.length - 1 ? list[idx + 1]!.censusDate : null;
        flat.push({
          kind: "data",
          row,
          groupFirst: idx === 0,
          groupLast: idx === list.length - 1,
          priorDate,
        });
        ring1 += row.ringOneCount ?? 0;
        ring2 += row.ringTwoCount ?? 0;
        ring3 += row.ringThreeCount ?? 0;
        palmsInspected += row.palmsInspectedCount ?? 0;
      });
      if (list.length > 0) {
        flat.push({
          kind: "subtotal",
          blockId: bId,
          totals: {
            ring1,
            ring2,
            ring3,
            totalBunches: ring1 + ring2 + ring3,
            palmsInspected,
            censusCount: list.length,
          },
        });
      }
    }
    return flat;
  }, [
    censusQ.data,
    blockById,
    filterCensusFrom,
    filterCensusTo,
    filterEstateId,
    filterDivisionId,
    filterBlockId,
  ]);
  const workers = workersQ.data?.data ?? [];
  const mandors = useMemo(
    () => workers.filter((w) => w.workerType === "MANDOR"),
    [workers],
  );
  const workerById = useMemo(() => {
    const m = new Map<string, Worker>();
    for (const w of workers) m.set(w.id, w);
    return m;
  }, [workers]);
  const gangById = useMemo(() => {
    const m = new Map<string, { id: string; code: string; name: string }>();
    for (const g of gangsQ.data?.data ?? []) m.set(g.id, g);
    return m;
  }, [gangsQ.data]);
  // Sub-blocks · for the Target sub-block column + the Add-block modal.
  const subBlocksQ = useQuery({
    queryKey: ["block-sub-blocks"],
    queryFn: () =>
      api.list<{ id: string; blockId: string; code: string; areaHa: string | null }>(
        "/v1/block-sub-blocks",
      ),
  });
  const subBlockById = useMemo(() => {
    const m = new Map<string, { id: string; blockId: string; code: string; areaHa: string | null }>();
    for (const s of subBlocksQ.data?.data ?? []) m.set(s.id, s);
    return m;
  }, [subBlocksQ.data]);
  const [showAddLine, setShowAddLine] = useState(false);

  const [generateSuccess, setGenerateSuccess] = useState<string | null>(null);

  const generateMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        plan: { id: string; totalBunchesExpected: number };
        lines: unknown[];
      }>("/v1/harvest-plans/generate", {
        planDate: from,
        ripeThreshold: 50,
      });
      return res.data;
    },
    onSuccess: async (data) => {
      setGenerateError(null);
      setGenerateSuccess(
        `Plan recalculated · ${data.lines.length} block(s) · ${data.plan.totalBunchesExpected} bunches expected`,
      );
      await qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
      await scheduleQ.refetch();
      setTimeout(() => setGenerateSuccess(null), 4000);
    },
    onError: (err) => {
      setGenerateSuccess(null);
      setGenerateError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const schedule = scheduleQ.data;

  // Single source of truth for "which schedule rows are visible" — the
  // header count and the table body must apply the exact same filters
  // (block/gang/status) or the count silently drifts from what's shown.
  const visibleScheduleRows = useMemo(() => {
    return (schedule?.scheduled ?? [])
      .filter((r) => matchesBlockFilter(r.line.blockId))
      .filter((r) =>
        filterGangId === "ALL"
          ? true
          : r.line.gangId === filterGangId ||
            r.line.mandorWorkerId === filterGangId,
      )
      .filter((r) =>
        filterStatus === "ALL" ? true : r.line.status === filterStatus,
      );
  }, [schedule, matchesBlockFilter, filterGangId, filterStatus]);

  return (
    <div className="space-y-6 text-sm">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-medium">Harvest Planning</h1>
          <p className="text-xs text-text-secondary">
            Continuous rolling schedule. Blocks are cut when ripe — no weekly
            boundary. Adjust the horizon to focus on what's coming.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => generateMut.mutate()}
            disabled={generateMut.isPending}
            className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs hover:bg-surface-1 disabled:opacity-50"
          >
            <IconRefresh
              size={14}
              className={generateMut.isPending ? "animate-spin" : ""}
            />
            {generateMut.isPending ? "Recalculating…" : "Recalc from census"}
          </button>
          <button
            onClick={async () => {
              const draftCount = (schedule?.scheduled ?? []).filter(
                (r) => r.line.status === "DRAFT",
              ).length;
              if (!draftCount) {
                alert("No DRAFT lines in the horizon to approve.");
                return;
              }
              if (
                !confirm(
                  `Approve plan?\n\n${draftCount} DRAFT line(s) in ${from} → ${to} will move to CONFIRMED. Manpower and vehicle assignment work from confirmed lines.`,
                )
              )
                return;
              try {
                const res = await api.post<{ confirmed: number }>(
                  "/v1/harvest-schedule/approve",
                  { from, to },
                );
                setGenerateSuccess(
                  `Plan approved · ${res.data.confirmed} line(s) confirmed`,
                );
                setTimeout(() => setGenerateSuccess(null), 4000);
                qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
              } catch (err) {
                alert(err instanceof ApiError ? err.message : String(err));
              }
            }}
            className="flex items-center gap-1 rounded bg-text-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
            title="Confirm every DRAFT line in the current horizon"
          >
            Approve plan
          </button>
          <button
            onClick={() => setShowCensusForm((v) => !v)}
            className="flex items-center gap-1 rounded bg-text-primary px-3 py-1.5 text-xs text-white hover:opacity-90"
          >
            <IconPlus size={14} /> Add census
          </button>
        </div>
      </div>

      {/* Horizon selector */}
      <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-surface-2 p-2">
        <div className="mr-2 text-xs text-text-secondary">Show:</div>
        {HORIZON_PRESETS.map((p) => {
          const isActive = !customMode && horizonDays === p.days;
          return (
            <button
              key={p.label}
              onClick={() => {
                setCustomMode(false);
                setHorizonDays(p.days);
              }}
              className={`rounded px-3 py-1 text-xs ${
                isActive
                  ? "bg-text-primary text-white"
                  : "border border-border hover:bg-surface-1"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        <button
          onClick={() => setCustomMode((v) => !v)}
          className={`rounded px-3 py-1 text-xs ${
            customMode
              ? "bg-text-primary text-white"
              : "border border-border hover:bg-surface-1"
          }`}
        >
          Custom
        </button>
        {customMode && (
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="rounded border border-border bg-surface-2 px-2 py-1 text-xs"
          />
        )}
        <div className="ml-auto text-xs text-text-secondary">
          {from} → {to}
        </div>
      </div>

      {/* KPI tiles */}
      {schedule && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
          <Kpi label="Blocks scheduled" value={schedule.totals.blocksScheduled} />
          <Kpi label="Est. bunches" value={schedule.totals.estBunches.toLocaleString()} />
          <Kpi
            label="Est. kg"
            value={Math.round(schedule.totals.estKg).toLocaleString()}
          />
          <Kpi
            label="Auto"
            value={schedule.totals.bySource.AUTO}
            tone="emerald"
          />
          <Kpi
            label="Edited"
            value={schedule.totals.bySource.EDITED}
            tone="amber"
          />
          <Kpi
            label="Candidates"
            value={schedule.totals.unscheduledCandidates}
            tone="slate"
          />
        </div>
      )}

      {/* AI reasoning banner (placeholder — agents wire later) */}
      <div className="flex items-start gap-2 rounded border border-text-accent/30 bg-bg-accent px-3 py-2 text-xs">
        <IconSparkles size={14} className="mt-0.5 text-text-accent" />
        <div>
          <div className="font-medium text-text-accent">
            AI Assistance · HYBRID mode
          </div>
          <div className="text-text-secondary">
            Estimator, Planner, and Assignor agents propose; you approve.
            Reasoning surfaces here once agents are wired. Configure in
            Tenant Settings → AI Assistance.
          </div>
        </div>
      </div>

      {generateError && (
        <div className="flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <IconAlertTriangle size={14} /> {generateError}
        </div>
      )}
      {generateSuccess && (
        <div className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          <IconRefresh size={14} /> {generateSuccess}
        </div>
      )}

      {/* Census entry form (collapsible) — Add mode */}
      {showCensusForm && (
        <MandorCensusForm
          blocks={plantedBlocks}
          divisions={divisionsQ.data?.data ?? []}
          mandors={mandors}
          defaultCensusDate={from}
          lastCensusByBlock={lastCensusByBlock}
          onSaved={() => {
            setCensusError(null);
            setShowCensusForm(false);
            setCensusSuccess("Census saved successfully.");
            setTimeout(() => setCensusSuccess(null), 4000);
            qc.invalidateQueries({ queryKey: ["mandor-census-recent"] });
            qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
          }}
          onCancel={() => {
            setShowCensusForm(false);
            setCensusError(null);
          }}
          onError={(msg) => setCensusError(msg)}
        />
      )}
      {censusSuccess && (
        <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          ✓ {censusSuccess}
        </div>
      )}

      {/* Census edit modal */}
      {editingCensus && (
        <MandorCensusEditModal
          census={editingCensus}
          blocks={plantedBlocks}
          mandors={mandors}
          onClose={() => setEditingCensus(null)}
          onSaved={() => {
            setEditingCensus(null);
            qc.invalidateQueries({ queryKey: ["mandor-census-recent"] });
            qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
          }}
        />
      )}
      {censusError && (
        <div className="text-xs text-red-600">{censusError}</div>
      )}

      {/* Shared filter bar — Estate / Division / Gang / Date range */}
      <FilterBar
        estateFilter={filterEstateId}
        setEstateFilter={setFilterEstateId}
        divisionFilter={filterDivisionId}
        setDivisionFilter={setFilterDivisionId}
        blockFilter={filterBlockId}
        setBlockFilter={setFilterBlockId}
        gangFilter={filterGangId}
        setGangFilter={setFilterGangId}
        statusFilter={filterStatus}
        setStatusFilter={setFilterStatus}
        dateFrom={filterCensusFrom}
        setDateFrom={setFilterCensusFrom}
        dateTo={filterCensusTo}
        setDateTo={setFilterCensusTo}
        estates={estatesQ.data?.data ?? []}
        divisions={availableDivisions}
        blocks={availableBlocks}
        gangs={gangsQ.data?.data ?? []}
      />

      {/* Recent census (last 7 days) — filtered */}
      <section className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div>
            <div className="flex items-center gap-1 font-medium">
              Recent Mandor census
              <RingInfoTip />
            </div>
            <div className="text-[10px] text-text-secondary">
              Ring 1 = ripe (cut now) · Ring 2 = sub-mature (3–5 days) ·
              Ring 3 = young (next round). Counts are <strong>bunches</strong>.
            </div>
          </div>
          <div className="text-xs text-text-secondary">Since {censusFromDate}</div>
        </div>
        {/* Wide table scrolls horizontally · Actions column stays pinned right */}
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Division</th>
              <th className="p-2 text-left">Block</th>
              <th
                className="p-2 text-right bg-surface-2/60"
                title="Block area (hectares planted) · reference from block master"
              >
                Block ha
              </th>
              <th
                className="p-2 text-right bg-surface-2/60"
                title="Total palm count on this block · reference from block master"
              >
                Block palms
              </th>
              <th
                className="p-2 text-right bg-surface-2/60"
                title="Stand per hectare = Block palms ÷ Block ha"
              >
                SPH
              </th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left" title="Previous census on this block">
                Last insp.
              </th>
              <th className="p-2 text-right" title="Ripe bunches ready to cut">
                Ring 1
              </th>
              <th className="p-2 text-right" title="Sub-mature — cut in 3–5 days">
                Ring 2
              </th>
              <th className="p-2 text-right" title="Young — next round">
                Ring 3
              </th>
              <th
                className="p-2 text-right"
                title="Ring 1 + Ring 2 + Ring 3"
              >
                Total bunches
              </th>
              <th
                className="p-2 text-right"
                title="Sample size · must be ≤ block's total palm count"
              >
                Palms insp.
              </th>
              <th
                className="p-2 text-right"
                title="Outstanding palms not yet inspected = Block palms − Palms insp."
              >
                O/S palms
              </th>
              <th
                className="p-2 text-right"
                title="Total bunches ÷ palms inspected · bunches per palm from this sample"
              >
                Bunch/palm
              </th>
              <th
                className="p-2 text-right"
                title="kg per hectare this round = Bunch/palm × SPH × ABW (avg bunch weight)"
              >
                Yield/ha
              </th>
              <th className="p-2 text-left">Target Period</th>
              <th className="p-2 text-left">Mandor</th>
              <th className="p-2 text-center">GPS</th>
              <th className="sticky right-0 border-l border-border bg-surface-1 p-2 text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {censusGroupedByBlock.map((entry, entryIdx) => {
              if (entry.kind === "subtotal") {
                const b = blockById.get(entry.blockId);
                const hectaresPlanted = b?.hectaresPlanted
                  ? Number(b.hectaresPlanted)
                  : null;
                const sph =
                  hectaresPlanted && hectaresPlanted > 0 && b?.palmCount
                    ? b.palmCount / hectaresPlanted
                    : null;
                const abw = Number(b?.averageBunchWeightKg ?? 22) || 22;
                const avgBunchPerPalm =
                  entry.totals.palmsInspected > 0
                    ? entry.totals.totalBunches / entry.totals.palmsInspected
                    : null;
                const yieldPerHa =
                  avgBunchPerPalm != null && sph != null
                    ? avgBunchPerPalm * sph * abw
                    : null;
                const outstanding =
                  b?.palmCount != null
                    ? Math.max(0, b.palmCount - entry.totals.palmsInspected)
                    : null;
                return (
                  <tr
                    key={`sub-${entry.blockId}`}
                    className="border-b-2 border-b-slate-300 bg-slate-50 text-[11px] font-semibold"
                  >
                    <td className="p-2" />
                    <td
                      className="p-2 font-mono text-text-secondary"
                      colSpan={2}
                      title={`Sub-total across ${entry.totals.censusCount} census row(s) on block ${b?.code ?? ""}`}
                    >
                      ↳ Sub-total · {b?.code ?? ""}
                    </td>
                    <td className="p-2 text-right tabular-nums text-text-secondary">
                      {b?.palmCount ?? "—"}
                    </td>
                    <td className="p-2 text-right tabular-nums text-text-secondary">
                      {sph != null ? sph.toFixed(1) : "—"}
                    </td>
                    <td className="p-2" colSpan={2} />
                    <td className="p-2 text-right tabular-nums">
                      {entry.totals.ring1}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {entry.totals.ring2}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {entry.totals.ring3}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {entry.totals.totalBunches}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {entry.totals.palmsInspected}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {outstanding ?? "—"}
                    </td>
                    <td
                      className="p-2 text-right tabular-nums"
                      title="Avg bunch/palm = total bunches ÷ total palms inspected across all census rows on this block"
                    >
                      {avgBunchPerPalm != null
                        ? avgBunchPerPalm.toFixed(2)
                        : "—"}
                    </td>
                    <td
                      className="p-2 text-right tabular-nums"
                      title="kg/ha = avg bunch/palm × SPH × ABW"
                    >
                      {yieldPerHa != null ? Math.round(yieldPerHa) : "—"}
                    </td>
                    <td className="p-2" colSpan={4} />
                  </tr>
                );
              }
              const { row: c, groupFirst, priorDate } = entry;
              const b = blockById.get(c.blockId);
              const divCode = b ? divisionCodeById.get(b.divisionId) : undefined;
              const overCount =
                b?.palmCount != null &&
                c.palmsInspectedCount != null &&
                c.palmsInspectedCount > b.palmCount;
              const totalBunches =
                (c.ringOneCount ?? 0) +
                (c.ringTwoCount ?? 0) +
                (c.ringThreeCount ?? 0);
              const yieldPerPalm =
                c.palmsInspectedCount && c.palmsInspectedCount > 0
                  ? totalBunches / c.palmsInspectedCount
                  : null;
              const abw = Number(b?.averageBunchWeightKg ?? 22) || 22;
              const hectaresPlanted = b?.hectaresPlanted
                ? Number(b.hectaresPlanted)
                : null;
              const sph =
                hectaresPlanted && hectaresPlanted > 0 && b?.palmCount
                  ? b.palmCount / hectaresPlanted
                  : null;
              // kg/ha this round = bunch/palm × SPH × ABW
              const yieldPerHa =
                yieldPerPalm != null && sph != null
                  ? yieldPerPalm * sph * abw
                  : null;
              const outstanding =
                b?.palmCount != null && c.palmsInspectedCount != null
                  ? Math.max(0, b.palmCount - c.palmsInspectedCount)
                  : null;
              return (
                <tr
                  key={c.id}
                  className={`border-b border-border ${groupFirst && entryIdx > 0 ? "border-t-2 border-t-slate-300" : ""}`}
                >
                  <td className="p-2 font-mono text-text-secondary">
                    {groupFirst ? divCode ?? "—" : ""}
                  </td>
                  <td
                    className={`p-2 font-mono ${groupFirst ? "font-semibold" : "text-text-secondary"}`}
                  >
                    {groupFirst ? b?.code ?? c.blockId.slice(0, 6) : "↳"}
                  </td>
                  <td className="p-2 text-right tabular-nums bg-surface-2/40 text-text-secondary">
                    {groupFirst ? hectaresPlanted?.toFixed(2) ?? "—" : ""}
                  </td>
                  <td className="p-2 text-right tabular-nums bg-surface-2/40 text-text-secondary">
                    {groupFirst ? b?.palmCount ?? "—" : ""}
                  </td>
                  <td className="p-2 text-right tabular-nums bg-surface-2/40 text-text-secondary">
                    {groupFirst ? (sph != null ? sph.toFixed(1) : "—") : ""}
                  </td>
                  <td className="p-2">{c.censusDate}</td>
                  <td className="p-2 text-text-secondary">
                    {priorDate ?? "—"}
                  </td>
                  <td className="p-2 text-right font-medium">
                    {c.ringOneCount}
                  </td>
                  <td className="p-2 text-right">{c.ringTwoCount}</td>
                  <td className="p-2 text-right">{c.ringThreeCount}</td>
                  <td className="p-2 text-right font-medium tabular-nums">
                    {totalBunches}
                  </td>
                  <td
                    className={`p-2 text-right tabular-nums ${overCount ? "bg-red-50 font-medium text-red-700" : ""}`}
                    title={
                      overCount
                        ? `Over block palm count (${b?.palmCount})`
                        : b?.palmCount
                          ? `Block total palms: ${b.palmCount}`
                          : ""
                    }
                  >
                    {c.palmsInspectedCount ?? "—"}
                    {overCount ? " ⚠" : ""}
                  </td>
                  <td
                    className="p-2 text-right tabular-nums text-text-secondary"
                    title={
                      b?.palmCount != null
                        ? `Block palms ${b.palmCount} − palms insp. ${c.palmsInspectedCount ?? 0} = ${outstanding}`
                        : "Block palm count missing"
                    }
                  >
                    {outstanding ?? "—"}
                  </td>
                  <td
                    className="p-2 text-right tabular-nums"
                    title="Total bunches ÷ palms inspected"
                  >
                    {yieldPerPalm != null ? yieldPerPalm.toFixed(2) : "—"}
                  </td>
                  <td
                    className="p-2 text-right tabular-nums"
                    title="kg/ha this round = Bunch/palm × SPH × ABW"
                  >
                    {yieldPerHa != null ? Math.round(yieldPerHa) : "—"}
                  </td>
                  <td className="p-2">{c.estMaturityDate ?? "—"}</td>
                  <td className="p-2">
                    {c.mandorWorkerId
                      ? workerById.get(c.mandorWorkerId)?.name ?? "—"
                      : "—"}
                  </td>
                  <td className="p-2 text-center">
                    {c.gpsPoint ? (
                      <IconMapPin
                        size={12}
                        className="mx-auto text-emerald-600"
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="sticky right-0 border-l border-border bg-surface-0 p-2">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingCensus(c)}
                        className="rounded bg-slate-800 px-2 py-1 text-white hover:bg-slate-900"
                        title={`Edit census · ${b?.code ?? ""} · ${c.censusDate}`}
                      >
                        <IconPencil size={11} />
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            !confirm(
                              `Delete census on block ${b?.code ?? "?"} · ${c.censusDate}?`,
                            )
                          )
                            return;
                          try {
                            await api.del(`/v1/mandor-census/${c.id}`);
                            qc.invalidateQueries({
                              queryKey: ["mandor-census-recent"],
                            });
                            qc.invalidateQueries({
                              queryKey: ["harvest-schedule"],
                            });
                          } catch (err) {
                            setCensusError(
                              err instanceof ApiError
                                ? err.message
                                : String(err),
                            );
                          }
                        }}
                        className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700"
                        title={`Delete census · ${b?.code ?? ""} · ${c.censusDate}`}
                      >
                        <IconTrash size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!censusGroupedByBlock.length && (
              <tr>
                <td
                  colSpan={19}
                  className="p-4 text-center text-text-secondary"
                >
                  No recent census. Click <strong>Add census</strong> above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </section>

      {/* Scheduled lines — filtered by same shared filter bar */}
      <section className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div className="font-medium">Scheduled blocks</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-text-secondary">
              {visibleScheduleRows.length} of {schedule?.scheduled.length ?? 0}{" "}
              in horizon
            </div>
            <button
              onClick={() => setShowAddLine(true)}
              className="flex items-center gap-1 rounded bg-text-primary px-3 py-1 text-xs text-white hover:opacity-90"
            >
              <IconPlus size={13} /> Add block
            </button>
          </div>
        </div>
        {/* Status legend (hover a chip for the definition) — this is
            the schedule-line lifecycle only. The physical FFB pipeline
            (bunch audit → mech pickup → SPB dispatch) tracks its own
            status separately, on the Delivery page. */}
        <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-surface-1 px-3 py-1.5">
          <span className="text-[10px] text-text-secondary">Status:</span>
          {(
            ["DRAFT", "CONFIRMED", "IN_PROGRESS", "DONE", "CANCELLED"] as const
          ).map((s) => (
            <span
              key={s}
              title={STATUS_META[s].desc}
              className={`cursor-help rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_META[s].text} bg-surface-2`}
            >
              {STATUS_META[s].label}
            </span>
          ))}
        </div>
        {/* Wide table scrolls horizontally · Actions column stays pinned right */}
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Block</th>
              <th className="p-2 text-left">Last census</th>
              <th className="p-2 text-left">Last harvest</th>
              <th className="p-2 text-left">Census R1/2/3</th>
              <th
                className="p-2 text-left"
                title="Scheduled cut date · drives dispatch & gang assignment. Defaults to the census's Target period on AUTO lines but planners can override."
              >
                Target date
              </th>
              <th
                className="p-2 text-left bg-surface-2/60"
                title="Mandor's estimated maturity date from the census (advisory). AUTO lines default the Target date to this value."
              >
                Target period
              </th>
              <th
                className="p-2 text-left"
                title="Target sub-block for this round · placeholder pending schema (harvest_plan_line.sub_block_id). Defaults to whole block."
              >
                Target sub-block
              </th>
              <th
                className="p-2 text-right bg-surface-2/60"
                title="Hectares scheduled for this round. Defaults to block's planted ha (whole block) unless the planner set a smaller target_ha."
              >
                Ha
              </th>
              <th className="p-2 text-right">Est. bunches</th>
              <th
                className="p-2 text-right"
                title="Estimated bunches × block ABW (average bunch weight, kg per bunch). ABW is a block reference, default 22 kg."
              >
                Est. kg
              </th>
              <th className="p-2 text-left">Mandor</th>
              <th
                className="p-2 text-left"
                title="Harvester(s) cutting the bunches. From line.harvesterWorkerIds."
              >
                Harvester
              </th>
              <th
                className="p-2 text-left"
                title="Carrier(s) moving bunches to TPH. From line.carrierWorkerIds."
              >
                Carrier
              </th>
              <th className="p-2 text-left" title="Assigned harvester gang">
                Gang
              </th>
              <th
                className="p-2 text-left bg-emerald-50"
                title="Date the cut actually happened · placeholder pending schema (harvest_plan_line.actual_harvest_date)."
              >
                Actual date
              </th>
              <th
                className="p-2 text-right bg-emerald-50"
                title="Actual bunches harvested (from field record). Compare vs Est. bunches for accuracy KPI."
              >
                Actual bunches
              </th>
              <th
                className="p-2 text-left"
                title="How this line was created · AUTO = generated from census · MANUAL = planner-added · EDITED = AUTO overridden by planner. Tracks planner override rate."
              >
                Source
              </th>
              <th className="sticky right-0 border-l border-border bg-surface-1 p-2 text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleScheduleRows.map((row) => {
              const c = row.census;
              const lastHarvest =
                row.block.lastHarvestDateCache ??
                c?.lastHarvestDateAtCensus ??
                null;
              const lastCensus =
                c?.censusDate ?? lastCensusByBlock.get(row.line.blockId);
              return (
                <tr key={row.line.id} className="border-b border-border">
                  <td className="p-2 font-mono">{row.block.code}</td>
                  <td className="p-2">
                    {c ? (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-800"
                        title={`Source census · ${c.censusDate}\nR1=${c.ringOneCount} · R2=${c.ringTwoCount} · R3=${c.ringThreeCount}\nPalms inspected: ${c.palmsInspectedCount ?? "?"}\nEst maturity: ${c.estMaturityDate ?? "n/a"}\nMandor: ${c.mandorWorkerId ? (workerById.get(c.mandorWorkerId)?.name ?? "?") : "n/a"}\n\nThis schedule line was created from that census (censusId ${c.id.slice(0, 8)}).`}
                      >
                        🔗 {c.censusDate}
                      </span>
                    ) : lastCensus ? (
                      <span
                        className="text-text-secondary"
                        title="No censusId on this line — this is the block's most recent census (not the one that drove scheduling)."
                      >
                        {lastCensus}
                      </span>
                    ) : (
                      <span
                        className="text-text-secondary"
                        title="No census linked to this line"
                      >
                        —
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-text-secondary">
                    {lastHarvest ?? "—"}
                  </td>
                  <td className="p-2 tabular-nums">
                    {c
                      ? `${c.ringOneCount} / ${c.ringTwoCount} / ${c.ringThreeCount}`
                      : "—"}
                  </td>
                  <td className="p-2">
                    {row.line.targetHarvestDate ?? (
                      <span className="text-text-secondary">unscheduled</span>
                    )}
                  </td>
                  <td
                    className="p-2 bg-surface-2/40 text-text-secondary tabular-nums"
                    title={
                      c?.estMaturityDate &&
                      row.line.targetHarvestDate &&
                      c.estMaturityDate !== row.line.targetHarvestDate
                        ? `Planner shifted the date. Census target: ${c.estMaturityDate} · Scheduled: ${row.line.targetHarvestDate}`
                        : ""
                    }
                  >
                    {c?.estMaturityDate ?? "—"}
                  </td>
                  <td className="p-2 text-text-secondary">
                    {row.line.subBlockId ? (
                      <span
                        className="font-mono font-medium text-text-primary"
                        title={`Sub-block ${subBlockById.get(row.line.subBlockId)?.code ?? ""} · ${subBlockById.get(row.line.subBlockId)?.areaHa ?? "?"} ha`}
                      >
                        {row.block.code}-
                        {subBlockById.get(row.line.subBlockId)?.code ?? "?"}
                      </span>
                    ) : (
                      <span title="No sub-block targeted — the whole block is scheduled. Use Add block to schedule a specific sub-block.">
                        whole block
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums bg-surface-2/40 text-text-secondary">
                    {row.line.targetHa
                      ? Number(row.line.targetHa).toFixed(2)
                      : row.block.hectaresPlanted
                        ? Number(row.block.hectaresPlanted).toFixed(2)
                        : "—"}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {row.line.estimatedBunches}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {Math.round(Number(row.line.estimatedKg))}
                  </td>
                  <td className="p-2">
                    {row.mandorWorker?.name ??
                      (row.line.mandorWorkerId
                        ? workerById.get(row.line.mandorWorkerId)?.name
                        : "—")}
                  </td>
                  <td
                    className="p-2 text-text-secondary"
                    title={renderWorkerListTooltip(
                      row.line.harvesterWorkerIds,
                      workerById,
                      "harvester",
                    )}
                  >
                    {renderWorkerListShort(
                      row.line.harvesterWorkerIds,
                      workerById,
                    )}
                  </td>
                  <td
                    className="p-2 text-text-secondary"
                    title={renderWorkerListTooltip(
                      row.line.carrierWorkerIds,
                      workerById,
                      "carrier",
                    )}
                  >
                    {renderWorkerListShort(
                      row.line.carrierWorkerIds,
                      workerById,
                    )}
                  </td>
                  <td className="p-2 text-text-secondary">
                    {row.line.gangId
                      ? gangById.get(row.line.gangId)?.code ??
                        gangById.get(row.line.gangId)?.name ??
                        "—"
                      : "—"}
                  </td>
                  <td className="p-2 bg-emerald-50/60 tabular-nums text-text-secondary">
                    {row.line.actualHarvestDate ?? "—"}
                  </td>
                  <td
                    className="p-2 text-right tabular-nums bg-emerald-50/60"
                    title={
                      row.line.actualBunches > 0 && row.line.estimatedBunches > 0
                        ? `${((row.line.actualBunches / row.line.estimatedBunches) * 100).toFixed(0)}% of estimate (${row.line.estimatedBunches})`
                        : "Actual bunches recorded on this line. 0 = not yet harvested."
                    }
                  >
                    {row.line.actualBunches > 0
                      ? row.line.actualBunches
                      : "—"}
                  </td>
                  <td className="p-2">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] ${SOURCE_BADGE[row.line.source]}`}
                      title={
                        row.line.source === "AUTO"
                          ? "Generated from census by algorithm"
                          : row.line.source === "MANUAL"
                            ? "Added manually by planner"
                            : "AUTO line overridden by planner"
                      }
                    >
                      {row.line.source}
                    </span>
                    <div
                      className={`mt-0.5 text-[9px] font-semibold ${STATUS_META[row.line.status].text}`}
                      title={STATUS_META[row.line.status].desc}
                    >
                      {STATUS_META[row.line.status].label}
                    </div>
                  </td>
                  <td className="sticky right-0 border-l border-border bg-surface-0 p-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingLineId(row.line.id)}
                        className="rounded bg-slate-800 px-2 py-1 text-white hover:bg-slate-900"
                        title="Edit schedule line"
                      >
                        <IconPencil size={12} />
                      </button>
                      {row.line.status !== "DONE" && (
                        <button
                          onClick={() => setCompletingLineId(row.line.id)}
                          className="rounded bg-blue-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-blue-700"
                          title={`Complete · record actuals for block ${row.block.code}`}
                        >
                          Complete
                        </button>
                      )}
                      {(c?.ringTwoCount ?? 0) > 0 &&
                        row.line.status !== "DONE" && (
                          <button
                            onClick={async () => {
                              if (
                                !confirm(
                                  `Suggest Round 2 for block ${row.block.code}?\n\nCreates a DRAFT line with target = ${row.line.targetHarvestDate ?? "today"} + 5 days, ${c?.ringTwoCount} Ring 2 bunches from census ${c?.censusDate}. Planner still needs to confirm after fresh census.`,
                                )
                              )
                                return;
                              try {
                                await api.post(
                                  `/v1/harvest-schedule/lines/${row.line.id}/suggest-r2`,
                                  {},
                                );
                                qc.invalidateQueries({
                                  queryKey: ["harvest-schedule"],
                                });
                              } catch (err) {
                                alert(
                                  err instanceof ApiError
                                    ? err.message
                                    : String(err),
                                );
                              }
                            }}
                            className="rounded bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700"
                            title={`Suggest R2 · ${c?.ringTwoCount} Ring 2 bunches available`}
                          >
                            R2
                          </button>
                        )}
                      <button
                        onClick={async () => {
                          const isDraft =
                            row.line.status === "DRAFT" &&
                            (row.line.actualBunches ?? 0) === 0;
                          if (
                            !confirm(
                              `${isDraft ? "Delete" : "Cancel"} schedule line for block ${row.block.code}${row.line.targetHarvestDate ? ` · ${row.line.targetHarvestDate}` : ""}?${isDraft ? "" : "\n\nLine has activity — it will be marked CANCELLED (kept for audit)."}`,
                            )
                          )
                            return;
                          try {
                            await api.del(
                              `/v1/harvest-schedule/lines/${row.line.id}`,
                            );
                            qc.invalidateQueries({
                              queryKey: ["harvest-schedule"],
                            });
                          } catch (err) {
                            alert(
                              err instanceof ApiError
                                ? err.message
                                : String(err),
                            );
                          }
                        }}
                        className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700"
                        title={`Delete / cancel schedule line · block ${row.block.code}`}
                      >
                        <IconTrash size={11} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!(schedule?.scheduled.length ?? 0) && (
              <tr>
                <td colSpan={18} className="p-4 text-center text-text-secondary">
                  No blocks scheduled in this horizon. Try Recalc from census.
                </td>
              </tr>
            )}
            {(schedule?.scheduled.length ?? 0) > 0 &&
              !visibleScheduleRows.length && (
                <tr>
                  <td
                    colSpan={18}
                    className="p-4 text-center text-text-secondary"
                  >
                    No lines match the current filters. Try Clear filters
                    above.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
        </div>
      </section>

      {/* Unscheduled candidates */}
      {(schedule?.candidates?.length ?? 0) > 0 && (
        <section className="rounded border border-border">
          <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
            <div className="font-medium">
              Candidates ({schedule?.candidates.length ?? 0})
            </div>
            <div className="text-xs text-text-secondary">
              Census recorded but not yet in the plan · click Schedule to
              promote a block, or Recalc for auto-generation (R1 ≥ 50)
            </div>
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-1 text-text-secondary">
              <tr>
                <th className="p-2 text-left">Block</th>
                <th className="p-2 text-left">Census date</th>
                <th className="p-2 text-right">R1</th>
                <th className="p-2 text-right">R2</th>
                <th className="p-2 text-right">R3</th>
                <th className="p-2 text-left">Est. maturity</th>
                <th className="p-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {(schedule?.candidates ?? []).map((c) => (
                <tr key={c.census.id} className="border-b border-border">
                  <td className="p-2 font-mono">{c.block.code}</td>
                  <td className="p-2">{c.census.censusDate}</td>
                  <td className="p-2 text-right font-medium">
                    {c.census.ringOneCount}
                  </td>
                  <td className="p-2 text-right text-text-secondary">
                    {c.census.ringTwoCount}
                  </td>
                  <td className="p-2 text-right text-text-secondary">
                    {c.census.ringThreeCount}
                  </td>
                  <td className="p-2">
                    {c.census.estMaturityDate ?? "—"}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={async () => {
                        if (
                          !confirm(
                            `Schedule block ${c.block.code}?\n\nCreates a DRAFT plan line with target = ${c.census.estMaturityDate ?? "today"}, ${c.census.ringOneCount} R1 bunches from census ${c.census.censusDate}. Planner can edit after.`,
                          )
                        )
                          return;
                        try {
                          await api.post(
                            `/v1/harvest-schedule/candidates/promote`,
                            { censusId: c.census.id },
                          );
                          qc.invalidateQueries({
                            queryKey: ["harvest-schedule"],
                          });
                        } catch (err) {
                          alert(
                            err instanceof ApiError
                              ? err.message
                              : String(err),
                          );
                        }
                      }}
                      className="rounded bg-emerald-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-emerald-700"
                      title={`Schedule block ${c.block.code} · target ${c.census.estMaturityDate ?? "today"}`}
                    >
                      Schedule
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Line edit modal */}
      {editingLineId && schedule && (
        <LineEditModal
          lineRow={
            schedule.scheduled.find((r) => r.line.id === editingLineId)!
          }
          workers={workers}
          gangs={gangsQ.data?.data ?? []}
          onClose={() => setEditingLineId(null)}
          onSaved={() => {
            setEditingLineId(null);
            qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
          }}
        />
      )}

      {/* Add block to schedule (create) */}
      {showAddLine && (
        <AddLineModal
          blocks={plantedBlocks}
          subBlocks={subBlocksQ.data?.data ?? []}
          gangs={gangsQ.data?.data ?? []}
          censuses={censusQ.data?.data ?? []}
          onClose={() => setShowAddLine(false)}
          onSaved={() => {
            setShowAddLine(false);
            qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
          }}
        />
      )}

      {/* Complete (execution) modal */}
      {completingLineId && schedule && (
        <CompleteLineModal
          lineRow={
            schedule.scheduled.find((r) => r.line.id === completingLineId)!
          }
          onClose={() => setCompletingLineId(null)}
          onSaved={() => {
            setCompletingLineId(null);
            qc.invalidateQueries({ queryKey: ["harvest-schedule"] });
            qc.invalidateQueries({ queryKey: ["blocks"] });
          }}
        />
      )}
    </div>
  );
}

// ==== Sub-components ====

function Kpi({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string | number;
  tone?: "default" | "emerald" | "amber" | "slate" | "red";
}) {
  const toneClass = {
    default: "text-text-primary",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    slate: "text-slate-600",
    red: "text-red-700",
  }[tone];
  return (
    <div className="rounded border border-border bg-surface-2 p-2">
      <div className="text-[10px] uppercase tracking-wide text-text-secondary">
        {label}
      </div>
      <div className={`text-xl font-medium tabular-nums ${toneClass}`}>
        {value}
      </div>
    </div>
  );
}

function MandorCensusForm({
  blocks,
  divisions,
  mandors,
  defaultCensusDate,
  lastCensusByBlock,
  onSaved,
  onCancel,
  onError,
}: {
  blocks: Block[];
  divisions: { id: string; code: string; name: string }[];
  mandors: Worker[];
  defaultCensusDate: string;
  lastCensusByBlock: Map<string, string>;
  onSaved: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [divisionId, setDivisionId] = useState("");
  const [blockId, setBlockId] = useState("");
  const [censusDate, setCensusDate] = useState(defaultCensusDate);
  const [ringOne, setRingOne] = useState("");
  const [ringTwo, setRingTwo] = useState("");
  const [ringThree, setRingThree] = useState("");
  const [palms, setPalms] = useState("");
  const [estMaturity, setEstMaturity] = useState("");
  const [mandorId, setMandorId] = useState("");
  const [gpsLat, setGpsLat] = useState("");
  const [gpsLng, setGpsLng] = useState("");
  const [saving, setSaving] = useState(false);

  // Selected block's palm count — drives (a) default value for the palms
  // field when a block is picked, and (b) validation that the entered
  // sample size doesn't exceed the block's total planted palms.
  const selectedBlock = blocks.find((b) => b.id === blockId);
  const blockPalmCap = selectedBlock?.palmCount ?? null;

  // When user picks a block, auto-fill palms with block.palmCount
  // (only if the user hasn't typed something manually). This is a soft
  // default — the field remains editable.
  useEffect(() => {
    if (!blockId) return;
    if (palms !== "") return;
    if (blockPalmCap == null) return;
    setPalms(String(blockPalmCap));
  }, [blockId, blockPalmCap, palms]);

  const palmsNum = palms ? Number(palms) : NaN;
  const palmsOverCap =
    blockPalmCap != null && Number.isFinite(palmsNum) && palmsNum > blockPalmCap;

  const submit = async () => {
    // Validate before hitting API so the user gets a clear message right
    // above the form (via onError → censusError banner).
    if (!blockId) {
      onError("Please pick a block before saving.");
      return;
    }
    if (!censusDate) {
      onError("Please pick a census date.");
      return;
    }
    if (palmsOverCap && blockPalmCap != null) {
      onError(
        `Palms inspected (${palmsNum}) exceeds the block's total palm count (${blockPalmCap}). Reduce the sample size or update the block's palm_count first.`,
      );
      return;
    }
    setSaving(true);
    try {
      const gps =
        gpsLat && gpsLng
          ? { lat: Number(gpsLat), lng: Number(gpsLng) }
          : undefined;
      await api.post("/v1/mandor-census", {
        blockId,
        censusDate,
        ringOneCount: Number(ringOne || 0),
        ringTwoCount: Number(ringTwo || 0),
        ringThreeCount: Number(ringThree || 0),
        palmsInspectedCount: palms ? Number(palms) : undefined,
        gpsPoint: gps,
        estMaturityDate: estMaturity || undefined,
        mandorWorkerId: mandorId || undefined,
      });
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const captureGps = () => {
    if (!navigator.geolocation) {
      onError("GPS not available in this browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLat(pos.coords.latitude.toFixed(6));
        setGpsLng(pos.coords.longitude.toFixed(6));
      },
      (err) => onError(`GPS: ${err.message}`),
    );
  };

  return (
    <div className="space-y-3 rounded border border-border bg-surface-2 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium">New Mandor census</div>
          <div className="text-[10px] text-text-secondary">
            Ring counts = number of <strong>bunches</strong> at each maturity
            stage (not palms).
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Division">
          <select
            value={divisionId}
            onChange={(e) => {
              setDivisionId(e.target.value);
              // Reset block if it doesn't belong to the new division
              const b = blocks.find((x) => x.id === blockId);
              if (b && e.target.value && b.divisionId !== e.target.value) {
                setBlockId("");
              }
            }}
            className="input"
          >
            <option value="">— all divisions —</option>
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} · {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Block">
          <select
            value={blockId}
            onChange={(e) => setBlockId(e.target.value)}
            className="input"
          >
            <option value="">— pick —</option>
            {blocks
              .filter((b) => !divisionId || b.divisionId === divisionId)
              .map((b) => {
                const last = lastCensusByBlock.get(b.id);
                return (
                  <option key={b.id} value={b.id}>
                    {b.code}
                    {b.palmCount ? ` · ${b.palmCount} palms` : ""}
                    {last ? ` · last census ${last}` : " · never censused"}
                  </option>
                );
              })}
          </select>
          {blockId && (
            <div className="mt-1 text-[10px] text-text-secondary">
              {lastCensusByBlock.get(blockId)
                ? `Last census on this block: ${lastCensusByBlock.get(blockId)}`
                : "No prior census on this block — this will be the first."}
            </div>
          )}
        </Field>
        <Field label="Census date">
          <input
            type="date"
            value={censusDate}
            onChange={(e) => setCensusDate(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Mandor (worker)">
          <select
            value={mandorId}
            onChange={(e) => setMandorId(e.target.value)}
            className="input"
          >
            <option value="">— pick —</option>
            {mandors.map((m) => (
              <option key={m.id} value={m.id}>
                {m.code} · {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target Harvesting Period">
          <input
            type="date"
            value={estMaturity}
            onChange={(e) => setEstMaturity(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Ring 1 bunches (ripe now)">
          <input
            type="number"
            min={0}
            value={ringOne}
            onChange={(e) => setRingOne(e.target.value)}
            className="input"
            placeholder="e.g. 120"
          />
        </Field>
        <Field label="Ring 2 bunches (3–5 days)">
          <input
            type="number"
            min={0}
            value={ringTwo}
            onChange={(e) => setRingTwo(e.target.value)}
            className="input"
            placeholder="e.g. 38"
          />
        </Field>
        <Field label="Ring 3 bunches (next round)">
          <input
            type="number"
            min={0}
            value={ringThree}
            onChange={(e) => setRingThree(e.target.value)}
            className="input"
            placeholder="e.g. 6"
          />
        </Field>
        <Field
          label={
            <span className="flex items-center gap-2">
              Palms inspected (sample)
              {blockPalmCap != null && (
                <span className="text-[10px] font-normal text-text-secondary">
                  · block cap {blockPalmCap}
                </span>
              )}
            </span>
          }
        >
          <input
            type="number"
            min={0}
            max={blockPalmCap ?? undefined}
            value={palms}
            onChange={(e) => setPalms(e.target.value)}
            className={`input ${palmsOverCap ? "border-red-500 bg-red-50" : ""}`}
            placeholder={blockPalmCap ? String(blockPalmCap) : "e.g. 80"}
          />
          {palmsOverCap && (
            <div className="mt-1 text-[10px] text-red-600">
              ⚠ Exceeds block total palm count ({blockPalmCap}). Reduce or
              update the block first.
            </div>
          )}
        </Field>
        <Field label="GPS lat">
          <input
            type="text"
            value={gpsLat}
            onChange={(e) => setGpsLat(e.target.value)}
            className="input"
            placeholder="e.g. 5.421823"
          />
        </Field>
        <Field label="GPS lng">
          <div className="flex gap-1">
            <input
              type="text"
              value={gpsLng}
              onChange={(e) => setGpsLng(e.target.value)}
              className="input flex-1"
              placeholder="e.g. 117.938451"
            />
            <button
              type="button"
              onClick={captureGps}
              className="rounded border border-border px-2 hover:bg-surface-1"
              title="Use device GPS"
            >
              <IconMapPin size={12} />
            </button>
          </div>
        </Field>
      </div>
      {!blockId && (
        <div className="text-[10px] text-text-secondary">
          Tip: pick a block above before saving.
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="rounded bg-text-primary px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save census"}
        </button>
      </div>
    </div>
  );
}

/**
 * Edit an existing census. Uses the same upsert endpoint — block+date
 * are the natural key, so re-POSTing updates counts.
 */
function MandorCensusEditModal({
  census,
  blocks,
  mandors,
  onClose,
  onSaved,
}: {
  census: MandorCensus;
  blocks: Block[];
  mandors: Worker[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const block = blocks.find((b) => b.id === census.blockId);
  const [ringOne, setRingOne] = useState(String(census.ringOneCount));
  const [ringTwo, setRingTwo] = useState(String(census.ringTwoCount));
  const [ringThree, setRingThree] = useState(String(census.ringThreeCount));
  const [palms, setPalms] = useState(
    census.palmsInspectedCount != null ? String(census.palmsInspectedCount) : "",
  );
  const [estMaturity, setEstMaturity] = useState(census.estMaturityDate ?? "");
  const [mandorId, setMandorId] = useState(census.mandorWorkerId ?? "");
  const [gpsLat, setGpsLat] = useState(
    census.gpsPoint ? String(census.gpsPoint.lat) : "",
  );
  const [gpsLng, setGpsLng] = useState(
    census.gpsPoint ? String(census.gpsPoint.lng) : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post("/v1/mandor-census", {
        blockId: census.blockId,
        censusDate: census.censusDate,
        ringOneCount: Number(ringOne || 0),
        ringTwoCount: Number(ringTwo || 0),
        ringThreeCount: Number(ringThree || 0),
        palmsInspectedCount: palms ? Number(palms) : undefined,
        gpsPoint:
          gpsLat && gpsLng
            ? { lat: Number(gpsLat), lng: Number(gpsLng) }
            : undefined,
        estMaturityDate: estMaturity || undefined,
        mandorWorkerId: mandorId || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded border border-border bg-surface-2 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <div className="text-sm font-medium">
            Edit census · Block {block?.code ?? "?"} · {census.censusDate}
          </div>
          <div className="text-[10px] text-text-secondary">
            Ring counts = bunches per maturity stage.
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Ring 1 bunches (ripe now)">
            <input
              type="number"
              min={0}
              value={ringOne}
              onChange={(e) => setRingOne(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Ring 2 bunches (3–5 days)">
            <input
              type="number"
              min={0}
              value={ringTwo}
              onChange={(e) => setRingTwo(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Ring 3 bunches (next round)">
            <input
              type="number"
              min={0}
              value={ringThree}
              onChange={(e) => setRingThree(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Palms inspected">
            <input
              type="number"
              min={0}
              value={palms}
              onChange={(e) => setPalms(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Target Harvesting Period">
            <input
              type="date"
              value={estMaturity}
              onChange={(e) => setEstMaturity(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Mandor (worker)">
            <select
              value={mandorId}
              onChange={(e) => setMandorId(e.target.value)}
              className="input"
            >
              <option value="">— pick —</option>
              {mandors.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="GPS lat">
            <input
              type="text"
              value={gpsLat}
              onChange={(e) => setGpsLat(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="GPS lng">
            <input
              type="text"
              value={gpsLng}
              onChange={(e) => setGpsLng(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded bg-text-primary px-3 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LineEditModal({
  lineRow,
  workers,
  gangs,
  onClose,
  onSaved,
}: {
  lineRow: HarvestScheduleResponse["scheduled"][number];
  workers: Worker[];
  gangs: { id: string; code: string; name: string; mandorWorkerId?: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Item (11): ABW × Est bunches = Est kg (auto-computed, editable).
  const abw = Number(lineRow.block.averageBunchWeightKg ?? 22) || 22;
  const [targetDate, setTargetDate] = useState(
    lineRow.line.targetHarvestDate ?? "",
  );
  const [targetHa, setTargetHa] = useState(lineRow.line.targetHa ?? "");
  const [estBunches, setEstBunches] = useState(
    String(lineRow.line.estimatedBunches ?? 0),
  );
  const [estKgOverride, setEstKgOverride] = useState<boolean>(false);
  const [estKg, setEstKg] = useState(lineRow.line.estimatedKg ?? "0");

  // Auto-compute Est Kg = ABW × Est bunches whenever bunches change,
  // unless the user has toggled override (Item 11 with escape hatch).
  useEffect(() => {
    if (estKgOverride) return;
    const n = Number(estBunches);
    if (Number.isFinite(n)) setEstKg((n * abw).toFixed(2));
  }, [estBunches, abw, estKgOverride]);
  const [mandorId, setMandorId] = useState(
    lineRow.line.mandorWorkerId ?? "",
  );
  const [harvesterIds, setHarvesterIds] = useState<string[]>(
    lineRow.line.harvesterWorkerIds ?? [],
  );
  const [carrierIds, setCarrierIds] = useState<string[]>(
    lineRow.line.carrierWorkerIds ?? [],
  );
  const [gangId, setGangId] = useState(lineRow.line.gangId ?? "");

  // Gang auto-link · picking a gang pulls its roster into the crew
  // fields so the planner SEES who is being assigned before saving.
  // MERGES with (never overwrites) any crew already hand-picked on
  // the line — union of current selection + gang roster.
  const assignGang = async (id: string) => {
    setGangId(id);
    if (!id) return;
    try {
      const res = await api.list<{
        worker: Worker;
      }>(`/v1/gangs/${id}/members`);
      const members = res.data.map((m) => m.worker);
      const union = (cur: string[], add: string[]) =>
        Array.from(new Set([...cur, ...add]));
      setHarvesterIds((cur) =>
        union(
          cur,
          members.filter((w) => w.workerType === "HARVESTER").map((w) => w.id),
        ),
      );
      setCarrierIds((cur) =>
        union(
          cur,
          members.filter((w) => w.workerType === "CARRIER").map((w) => w.id),
        ),
      );
      const gangRow = gangs.find((g) => g.id === id);
      const gangMandor =
        gangRow?.mandorWorkerId ??
        members.find((w) => w.workerType === "MANDOR")?.id;
      // Only fill the mandor if none is picked yet — don't overwrite.
      if (gangMandor) setMandorId((cur) => cur || gangMandor);
    } catch {
      // roster fetch failure is non-fatal · backend will still autofill
    }
  };
  const [durationHrs, setDurationHrs] = useState(
    lineRow.line.estDurationHours ?? "",
  );
  const [editReason, setEditReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const mandors = workers.filter((w) => w.workerType === "MANDOR");
  const harvesters = workers.filter((w) => w.workerType === "HARVESTER");
  const carriers = workers.filter((w) => w.workerType === "CARRIER");

  const toggle = (id: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/v1/harvest-schedule/lines/${lineRow.line.id}`, {
        targetHarvestDate: targetDate || undefined,
        targetHa: targetHa || undefined,
        estimatedBunches: Number(estBunches),
        estimatedKg: estKg,
        estDurationHours: durationHrs || undefined,
        gangId: gangId || null,
        mandorWorkerId: mandorId || null,
        harvesterWorkerIds: harvesterIds,
        carrierWorkerIds: carrierIds,
        editReason: editReason || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      {/* Item (9): max-h + overflow-y-auto so the Save button stays reachable
          on short viewports. sticky footer keeps Save/Cancel always visible. */}
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded border border-border bg-surface-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div>
            <div className="text-sm font-medium">
              Edit schedule · Block {lineRow.block.code}
            </div>
            <div className="text-xs text-text-secondary">
              {lineRow.block.hectaresPlanted ?? "?"} ha planted ·{" "}
              {lineRow.block.palmCount ?? "?"} palms
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 hover:bg-surface-1"
          >
            ×
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Target harvest date">
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Target ha">
            <input
              type="number"
              step="0.01"
              value={targetHa}
              onChange={(e) => setTargetHa(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Est duration (hrs)">
            <input
              type="number"
              step="0.5"
              value={durationHrs}
              onChange={(e) => setDurationHrs(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Est bunches">
            <input
              type="number"
              value={estBunches}
              onChange={(e) => setEstBunches(e.target.value)}
              className="input"
            />
          </Field>
          <Field
            label={
              <span className="flex items-center gap-2">
                Est kg{" "}
                <span className="text-[10px] font-normal text-text-secondary">
                  (ABW {abw} × bunches)
                </span>
                <label className="flex items-center gap-1 text-[10px] font-normal text-text-secondary">
                  <input
                    type="checkbox"
                    checked={estKgOverride}
                    onChange={(e) => setEstKgOverride(e.target.checked)}
                  />
                  Override
                </label>
              </span>
            }
          >
            <input
              type="number"
              value={estKg}
              onChange={(e) => setEstKg(e.target.value)}
              readOnly={!estKgOverride}
              className={`input ${!estKgOverride ? "opacity-70" : ""}`}
            />
          </Field>
          <Field label="Gang (auto-fills crew)">
            <select
              value={gangId}
              onChange={(e) => void assignGang(e.target.value)}
              className="input"
            >
              <option value="">— no gang —</option>
              {gangs.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.code} · {g.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Mandor">
            <select
              value={mandorId}
              onChange={(e) => setMandorId(e.target.value)}
              className="input"
            >
              <option value="">— pick —</option>
              {mandors.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-xs text-text-secondary">
              Harvesters ({harvesterIds.length})
            </div>
            <div className="max-h-32 overflow-y-auto rounded border border-border p-2">
              {harvesters.map((h) => (
                <label key={h.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={harvesterIds.includes(h.id)}
                    onChange={() =>
                      toggle(h.id, harvesterIds, setHarvesterIds)
                    }
                  />
                  {h.code} · {h.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-text-secondary">
              Carriers ({carrierIds.length})
            </div>
            <div className="max-h-32 overflow-y-auto rounded border border-border p-2">
              {carriers.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={carrierIds.includes(c.id)}
                    onChange={() => toggle(c.id, carrierIds, setCarrierIds)}
                  />
                  {c.code} · {c.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        <Field label="Reason for edit (optional but recommended)">
          <input
            type="text"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            className="input"
            placeholder="e.g. Rain forecast · pushed +1 day"
          />
        </Field>

        {error && (
          <div className="mt-2 text-xs text-red-600">{error}</div>
        )}
        </div>
        {/* Sticky footer — always visible even when body overflows (Item 9) */}
        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-border bg-surface-2 p-3">
          <button
            onClick={onClose}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded bg-text-primary px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <IconCalendarEvent size={12} className="mr-1 inline" />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-text-secondary">
        {label}
      </div>
      {children}
    </label>
  );
}

/** Small info tooltip explaining Ring 1/2/3 semantics. */
function RingInfoTip() {
  return (
    <span
      title={
        "Ring 1 · ripe bunches, cut in this round\n" +
        "Ring 2 · sub-mature, cut in the next 3–5 days\n" +
        "Ring 3 · young, next planning round\n" +
        "Counts are BUNCHES, not palms."
      }
      className="cursor-help text-text-secondary"
    >
      <IconInfoCircle size={12} />
    </span>
  );
}

/**
 * Shared filter bar for census + schedule sections (items 13, 14).
 * Estate → Division cascades. Gang and date range added for census /
 * schedule filtering.
 */
function FilterBar({
  estateFilter,
  setEstateFilter,
  divisionFilter,
  setDivisionFilter,
  blockFilter,
  setBlockFilter,
  gangFilter,
  setGangFilter,
  statusFilter,
  setStatusFilter,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  estates,
  divisions,
  blocks,
  gangs,
}: {
  estateFilter: string;
  setEstateFilter: (v: string) => void;
  divisionFilter: string;
  setDivisionFilter: (v: string) => void;
  blockFilter: string;
  setBlockFilter: (v: string) => void;
  gangFilter: string;
  setGangFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  estates: { id: string; code: string; name: string }[];
  divisions: { id: string; code: string; name: string }[];
  blocks: { id: string; code: string }[];
  gangs: { id: string; code: string; name: string }[];
}) {
  const anyActive =
    estateFilter !== "ALL" ||
    divisionFilter !== "ALL" ||
    blockFilter !== "ALL" ||
    gangFilter !== "ALL" ||
    statusFilter !== "ALL" ||
    !!dateFrom ||
    !!dateTo;
  const clearAll = () => {
    setEstateFilter("ALL");
    setDivisionFilter("ALL");
    setBlockFilter("ALL");
    setGangFilter("ALL");
    setStatusFilter("ALL");
    setDateFrom("");
    setDateTo("");
  };
  const active = (v: string, empty: string) =>
    v !== empty
      ? "border-emerald-500 bg-emerald-50 font-medium"
      : "border-border bg-surface-2";
  return (
    <div className="flex flex-wrap items-end gap-2 rounded border border-border bg-surface-2 p-2 text-xs">
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Estate</span>
        <select
          value={estateFilter}
          onChange={(e) => setEstateFilter(e.target.value)}
          className={`rounded border px-2 py-1 ${active(estateFilter, "ALL")}`}
        >
          <option value="ALL">All estates</option>
          {estates.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Division</span>
        <select
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
          className={`rounded border px-2 py-1 ${active(divisionFilter, "ALL")}`}
        >
          <option value="ALL">All divisions</option>
          {divisions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.code} · {d.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Block</span>
        <select
          value={blockFilter}
          onChange={(e) => setBlockFilter(e.target.value)}
          className={`rounded border px-2 py-1 ${active(blockFilter, "ALL")}`}
        >
          <option value="ALL">All blocks</option>
          {blocks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Gang / Mandor</span>
        <select
          value={gangFilter}
          onChange={(e) => setGangFilter(e.target.value)}
          className={`rounded border px-2 py-1 ${active(gangFilter, "ALL")}`}
        >
          <option value="ALL">All gangs</option>
          {gangs.map((g) => (
            <option key={g.id} value={g.id}>
              {g.code} · {g.name}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Status</span>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`rounded border px-2 py-1 ${active(statusFilter, "ALL")}`}
        >
          <option value="ALL">All statuses</option>
          {(
            ["DRAFT", "CONFIRMED", "IN_PROGRESS", "DONE", "CANCELLED"] as const
          ).map((s) => (
            <option key={s} value={s} title={STATUS_META[s].desc}>
              {STATUS_META[s].label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Census date from</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={`rounded border px-2 py-1 ${active(dateFrom, "")}`}
        />
      </label>
      <label className="flex flex-col">
        <span className="text-[10px] text-text-secondary">Census date to</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={`rounded border px-2 py-1 ${active(dateTo, "")}`}
        />
      </label>
      {anyActive && (
        <button
          onClick={clearAll}
          className="ml-auto rounded border border-emerald-600 px-2 py-1 text-emerald-800 hover:bg-emerald-100"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// ==== Complete (execution) modal ====
function CompleteLineModal({
  lineRow,
  onClose,
  onSaved,
}: {
  lineRow: HarvestScheduleResponse["scheduled"][number];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { line, block } = lineRow;
  const abw = Number(block.averageBunchWeightKg ?? 22) || 22;
  const [actualBunches, setActualBunches] = useState<number>(
    line.estimatedBunches ?? 0,
  );
  const [actualKg, setActualKg] = useState<string>(
    Math.round((line.estimatedBunches ?? 0) * abw).toString(),
  );
  const [actualDate, setActualDate] = useState<string>(
    line.targetHarvestDate ?? new Date().toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState("");
  const [autoKg, setAutoKg] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-recompute kg when bunches change and autoKg is on.
  useEffect(() => {
    if (autoKg) setActualKg(Math.round(actualBunches * abw).toString());
  }, [actualBunches, abw, autoKg]);

  const delta = actualBunches - (line.estimatedBunches ?? 0);
  const deltaPct = line.estimatedBunches
    ? Math.round((delta / line.estimatedBunches) * 100)
    : null;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.post(`/v1/harvest-schedule/lines/${line.id}/complete`, {
        actualBunches,
        actualKg: Number(actualKg),
        actualHarvestDate: actualDate,
        notes: notes || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded border border-border bg-surface-0 shadow-lg">
        <div className="border-b border-border bg-surface-2 px-4 py-2">
          <div className="font-medium">
            Complete · Block {block.code}
          </div>
          <div className="text-xs text-text-secondary">
            Record actuals · marks status DONE and creates a harvest record
          </div>
        </div>
        <div className="space-y-3 p-4 text-xs">
          <div className="rounded border border-border bg-surface-1 px-2 py-1.5 text-text-secondary">
            <span className="font-medium text-text-primary">Estimate:</span>{" "}
            {line.estimatedBunches} bunches ·{" "}
            {Math.round(Number(line.estimatedKg))} kg · target{" "}
            {line.targetHarvestDate ?? "—"}
          </div>

          <label className="flex flex-col gap-1">
            <span className="font-medium">Actual bunches</span>
            <input
              type="number"
              min={1}
              step={1}
              value={actualBunches}
              onChange={(e) => setActualBunches(Number(e.target.value) || 0)}
              className="rounded border border-border px-2 py-1"
            />
            {deltaPct !== null && actualBunches > 0 && (
              <span
                className={
                  Math.abs(deltaPct) > 30
                    ? "text-amber-700"
                    : "text-text-secondary"
                }
              >
                {delta >= 0 ? "+" : ""}
                {delta} vs estimate ({deltaPct}%)
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium">Actual kg</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step="0.01"
                value={actualKg}
                onChange={(e) => {
                  setAutoKg(false);
                  setActualKg(e.target.value);
                }}
                className="w-40 rounded border border-border px-2 py-1"
              />
              <label className="flex items-center gap-1 text-text-secondary">
                <input
                  type="checkbox"
                  checked={autoKg}
                  onChange={(e) => setAutoKg(e.target.checked)}
                />
                auto (bunches × {abw} kg ABW)
              </label>
            </div>
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium">Actual harvest date</span>
            <input
              type="date"
              value={actualDate}
              onChange={(e) => setActualDate(e.target.value)}
              className="rounded border border-border px-2 py-1"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium">Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="rounded border border-border px-2 py-1"
              placeholder="Weather delay, gang short-handed, TPH rejection, etc."
            />
          </label>

          {error && (
            <div className="rounded border border-red-300 bg-red-50 p-2 text-red-800">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-surface-2 px-4 py-2">
          <button
            onClick={onClose}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || actualBunches <= 0}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Mark complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==== Add block to schedule (create) ====
// The block → sub-block → schedule route. Defaults flow from masters:
// ha ← sub-block area (else block planted ha) · bunches ← latest census
// R1 · kg ← bunches × ABW. Gang assignment auto-fills the crew
// server-side (same rule as the edit modal).
function AddLineModal({
  blocks,
  subBlocks,
  gangs,
  censuses,
  onClose,
  onSaved,
}: {
  blocks: Block[];
  subBlocks: { id: string; blockId: string; code: string; areaHa: string | null }[];
  gangs: { id: string; code: string; name: string }[];
  censuses: MandorCensus[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [blockId, setBlockId] = useState("");
  const [subBlockId, setSubBlockId] = useState("");
  const [targetDate, setTargetDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [gangId, setGangId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const block = blocks.find((b) => b.id === blockId);
  const blockSubs = subBlocks.filter((s) => s.blockId === blockId);
  const sub = blockSubs.find((s) => s.id === subBlockId);
  const latestCensus = censuses
    .filter((c) => c.blockId === blockId)
    .sort((a, b) => b.censusDate.localeCompare(a.censusDate))[0];

  // Live defaults preview — what the backend will store.
  const defaultHa = sub?.areaHa
    ? Number(sub.areaHa)
    : block?.hectaresPlanted
      ? Number(block.hectaresPlanted)
      : null;
  const defaultBunches = latestCensus?.ringOneCount ?? 0;
  const abw = Number(block?.averageBunchWeightKg ?? 22) || 22;
  const defaultKg = defaultBunches * abw;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.post("/v1/harvest-schedule/lines", {
        blockId,
        subBlockId: subBlockId || null,
        targetHarvestDate: targetDate,
        gangId: gangId || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded border border-border bg-surface-0 shadow-lg">
        <div className="border-b border-border bg-surface-2 px-4 py-2">
          <div className="font-medium">Add block to schedule</div>
          <div className="text-xs text-text-secondary">
            Block → sub-block → date + gang · ha, bunches and kg default
            from masters and the latest census
          </div>
        </div>
        <div className="space-y-3 p-4 text-xs">
          <label className="flex flex-col gap-1">
            <span className="font-medium">Block</span>
            <select
              value={blockId}
              onChange={(e) => {
                setBlockId(e.target.value);
                setSubBlockId("");
              }}
              className="rounded border border-border px-2 py-1.5"
            >
              <option value="">— select block —</option>
              {blocks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} · {b.hectaresPlanted ?? "?"} ha ·{" "}
                  {b.palmCount ?? "?"} palms
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-medium">
              Sub-block{" "}
              <span className="font-normal text-text-secondary">
                (optional · whole block if empty)
              </span>
            </span>
            <select
              value={subBlockId}
              onChange={(e) => setSubBlockId(e.target.value)}
              disabled={!blockId || !blockSubs.length}
              className="rounded border border-border px-2 py-1.5 disabled:opacity-50"
            >
              <option value="">
                {blockId && !blockSubs.length
                  ? "— no sub-blocks defined (Palms tab) —"
                  : "— whole block —"}
              </option>
              {blockSubs.map((s) => (
                <option key={s.id} value={s.id}>
                  {block?.code}-{s.code} · {s.areaHa ?? "?"} ha
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="font-medium">Target harvest date</span>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="rounded border border-border px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="font-medium">Harvester gang</span>
              <select
                value={gangId}
                onChange={(e) => setGangId(e.target.value)}
                className="rounded border border-border px-2 py-1.5"
              >
                <option value="">— no gang —</option>
                {gangs.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.code} · {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {blockId && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2.5 text-emerald-900">
              <div className="mb-1 font-medium">Defaults (auto)</div>
              <div className="grid grid-cols-3 gap-2 tabular-nums">
                <div>
                  Ha:{" "}
                  <span className="font-medium">
                    {defaultHa != null ? defaultHa.toFixed(2) : "—"}
                  </span>
                </div>
                <div>
                  Bunches:{" "}
                  <span className="font-medium">{defaultBunches}</span>
                </div>
                <div>
                  Kg:{" "}
                  <span className="font-medium">{Math.round(defaultKg)}</span>
                </div>
              </div>
              <div className="mt-1 text-[10px]">
                {latestCensus
                  ? `From census ${latestCensus.censusDate} (R1=${latestCensus.ringOneCount}) × ABW ${abw} kg`
                  : "No census on this block yet — bunches default to 0; edit after adding."}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded border border-red-300 bg-red-50 p-2 text-red-800">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-surface-2 px-4 py-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !blockId || !targetDate}
            className="rounded bg-text-primary px-3 py-1 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Adding…" : "Add to schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
